import { forwardRef, ReactNode, useState } from "react";

// eslint-disable-next-line no-restricted-imports
import { Stack, SxProps, Theme, useTheme } from "@mui/material";
import ButtonBase from "@mui/material/ButtonBase";

import { mergeSx } from "@/common/helpers/mergeSx";
import { useMediaQuery } from "@/common/hooks/useMediaQuery";
import { useTranslate } from "@/common/hooks/useTranslate";
import { FormIcon } from "@/forms-design-system/FormIcon";
import { FormTypography } from "@/forms-design-system/FormTypography/FormTypography";
import { InvalidThemeErrorMessage } from "@/forms-design-system/InvalidThemeErrorMessage";
import { ChevronDown, ChevronUp } from "@/icons/outlined";

export interface FormWelcomeBannerProps {
  memberName: string;
  details?: ReactNode;
  sx?: SxProps<Theme>;
}

export const FormWelcomeBanner = forwardRef<HTMLDivElement, FormWelcomeBannerProps>(
  ({ memberName, details, sx }, ref) => {
    const theme = useTheme();
    const { t } = useTranslate();
    const { isPhoneScreen } = useMediaQuery();
    const [expanded, setExpanded] = useState(true);

    if (!theme.palette.forms) {
      return <InvalidThemeErrorMessage />;
    }

    const welcomeText = t("common", "memberBanner.welcomeBack", { name: memberName });

    const containerStyles: SxProps<Theme> = mergeSx(
      {
        position: "relative",
        width: "100%",
        boxSizing: "border-box",
        backgroundColor: isPhoneScreen ? theme.palette.forms.surface.supershy : "transparent",
        borderBottom: `1px solid ${theme.palette.forms.border.quiet}`,
        px: 3,
        py: isPhoneScreen ? 1.5 : 2.5,
        display: "flex",
        alignItems: "center",
        justifyContent: isPhoneScreen ? "space-between" : "center",
        overflow: "hidden",
      },
      sx
    );

    if (isPhoneScreen) {
      return (
        <Stack ref={ref} sx={containerStyles} direction="row">
          <Stack>
            <Stack direction="row" alignItems="center" gap={1.5}>
              <FormTypography variant="subtitle2" vibe="intense">
                {welcomeText}
              </FormTypography>
              <FormTypography variant="subtitle1" vibe="intense">
                👋
              </FormTypography>
            </Stack>
            {details && expanded && details}
          </Stack>
          {details && (
            <ButtonBase disableRipple onClick={() => setExpanded((prev) => !prev)} sx={{ p: 0.5, borderRadius: 1 }}>
              <FormIcon size="small" color={theme.palette.forms.text.brand}>
                {expanded ? <ChevronUp /> : <ChevronDown />}
              </FormIcon>
            </ButtonBase>
          )}
        </Stack>
      );
    }

    return (
      <Stack ref={ref} sx={containerStyles} direction="row" gap={1.25}>
        <Stack alignItems="center">
          <Stack direction="row" alignItems="center" gap={1.5}>
            <FormTypography variant="h6" vibe="intense">
              👋
            </FormTypography>
            <FormTypography variant="h6" vibe="intense">
              {welcomeText}
            </FormTypography>
          </Stack>
          {details}
        </Stack>
      </Stack>
    );
  }
);

FormWelcomeBanner.displayName = "FormWelcomeBanner";
